import { AutoComplete } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { ApiClientContext, UserContext } from "../../../../../../App";
import { TimeTrackingUserDataContext } from "../../../TimeTrackingUserDataProvider";
import { TrackerContext } from "../utils/types";

interface SessionSuggestion {
  description: string;
  projectId: string;
  projectName: string;
  projectColor: string;
}

interface DescriptionAutocompleteProps {
  disabled?: boolean;
}

export const DescriptionAutocomplete: React.FC<
  DescriptionAutocompleteProps
> = ({ disabled }) => {
  const {
    formData,
    setFormData,
    isTracking,
    updateCurrentSessionDescription,
    updateCurrentSessionProject,
  } = useContext(TrackerContext);
  const { apiClient } = useContext(ApiClientContext);
  const { user } = useContext(UserContext);
  const { projects } = useContext(TimeTrackingUserDataContext);

  const [value, setValue] = useState(formData.description ?? "");
  const [suggestions, setSuggestions] = useState<SessionSuggestion[]>([]);

  useEffect(() => {
    setValue(formData.description ?? "");
  }, [formData.description]);

  useEffect(() => {
    if (!user?.id) return;
    apiClient
      .get(`/api/time-tracking/sessions/suggestions?userId=${user.id}`)
      .then((res) => setSuggestions(res.data ?? []))
      .catch(() => setSuggestions([]));
  }, [user?.id]);

  const projectIds = new Set(projects.map((p) => p.projectId));

  const filteredOptions = suggestions
    .filter(
      (s) =>
        projectIds.has(s.projectId) &&
        s.description?.toLowerCase().includes(value.toLowerCase()),
    )
    .map((s) => ({
      value: s.description,
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: s.projectColor,
              flexShrink: 0,
              display: "inline-block",
            }}
          />
          <span style={{ fontWeight: 500 }}>{s.description}</span>
          <span
            style={{
              color: s.projectColor,
              fontSize: 12,
              marginLeft: "auto",
              fontWeight: 500,
            }}
          >
            {s.projectName}
          </span>
        </div>
      ),
      suggestion: s,
    }));

  const handleSelect = (selectedValue: string, option: any) => {
    const suggestion: SessionSuggestion = option.suggestion;
    setValue(suggestion.description);
    if (isTracking) {
      if (suggestion.description !== formData.description) {
        updateCurrentSessionDescription(suggestion.description);
      }
      if (suggestion.projectId !== formData.projectId) {
        updateCurrentSessionProject(suggestion.projectId);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        description: suggestion.description,
        projectId: suggestion.projectId,
      }));
    }
  };

  const handleBlur = () => {
    if (isTracking && value !== formData.description) {
      updateCurrentSessionDescription(value);
    } else if (!isTracking) {
      setFormData((prev) => ({ ...prev, description: value }));
    }
  };

  return (
    <AutoComplete
      style={{ flex: 1, minWidth: 120 }}
      value={value}
      options={filteredOptions}
      onChange={(val) => setValue(val)}
      onSelect={handleSelect}
      onBlur={handleBlur}
      disabled={disabled}
      maxLength={3000}
    >
      <input
        style={{
          width: "100%",
          border: "1px solid #d9d9d9",
          borderRadius: 6,
          boxShadow: "none",
          fontSize: 15,
          padding: "4px 11px",
          outline: "none",
        }}
        placeholder="What are you working on?"
        onKeyDown={(e) => {
          if (e.key === "Enter") handleBlur();
        }}
      />
    </AutoComplete>
  );
};
